import ProjectsRepo from "@/repositories/projectsRepository";

import {
  fetchMedia,
  deleteMedia,
  fetchImageMedia,
  fetchSoftdeletedMedia,
  createSketch,
  updateSketch,
  createImageMedia
} from "../../repositories/projectsRepository";
import idb from "@/plugins/indexdb.js";

export default {
  namespaced: true,

  state: {
    selectedMedia: null,
    drawing: null,
    backgroundLayer: null,
    finalImage: null,

    medias: [],
    loading: false,
    error: null
  },

  mutations: {
    SET_SELECTED_MEDIA(state, media_uuid) {
      Object.assign(state, { selectedMedia: media_uuid });
    },

    SET_BACKGROUND_LAYER(state, bg_layer) {
      state.backgroundLayer = bg_layer;
    },

    SET_DRAWING(state, drawing) {
      Object.assign(state, { drawing });
    },

    SET_FINAL_IMAGE(state, final_image) {
      Object.assign(state, { finalImage: final_image });
    },

    SET_MEDIAS(state, medias) {
      state.medias = medias;
    },

    MEDIAS_REQUEST(state) {
      Object.assign(state, { loading: true, error: null });
    },

    MEDIAS_ERROR(state, payload) {
      Object.assign(state, {
        loading: false,
        error: payload || true
      });
    },

    MEDIAS_SUCCESS(state) {
      Object.assign(state, { loading: false, error: null });
    }
  },

  actions: {
    // Fetch a media from server
    async fetchMedia({ commit }, { project_uuid, media_uuid }) {
      const result = await fetchMedia(project_uuid, media_uuid).catch(e => {
        commit("MEDIAS_ERROR", e);
        const errMsg = `Média non trouvé. UUID: ${project_uuid} | MEDIA_UUID: ${media_uuid}`;
        return Promise.reject(errMsg);
      });

      if (result) {
        commit("MEDIAS_SUCCESS");
      }

      return result;
    },

    // Delete a media from server
    async deleteMedia({ commit }, { project_uuid, media_uuid }) {
      const result = await deleteMedia(project_uuid, media_uuid).catch(e => {
        commit("MEDIAS_ERROR", e);
        const errMsg = `Média non trouvé. UUID: ${project_uuid} | MEDIA_UUID: ${media_uuid}`;
        return Promise.reject(errMsg);
      });

      if (result) {
        commit("MEDIAS_SUCCESS");
      }

      return result;
    },

    // Fetch media image
    async fetchImageMedia({ commit }, { project_uuid, media_uuid }) {
      const result = await fetchImageMedia(project_uuid, media_uuid).catch(
        e => {
          commit("MEDIAS_ERROR", e);
          return Promise.reject(e);
        }
      );

      if (result) {
        commit("MEDIAS_SUCCESS");
      }

      return result;
    },

    // Fetch soft deleted media
    async fetchSoftDeletedMedia({ commit }, { project_uuid, media_uuid }) {
      const result = await fetchSoftdeletedMedia(
        project_uuid,
        media_uuid
      ).catch(e => {
        commit("MEDIAS_ERROR", e);
        return Promise.reject(e);
      });

      if (result) {
        commit("MEDIAS_SUCCESS");
      }

      return result;
    },

    // Create a sketch media
    async createSketchMedia({ commit }, { project_uuid, media_uuid, payload }) {
      const result = await createSketch(
        project_uuid,
        media_uuid,
        payload
      ).catch(e => {
        commit("MEDIAS_ERROR", e);
        return Promise.reject(e);
      });

      if (result) {
        commit("MEDIAS_SUCCESS");
      }

      return result;
    },

    // Update sketch in server
    async updateSketchMedia({ commit }, { project_uuid, media_uuid, payload }) {
      const result = await updateSketch(
        project_uuid,
        media_uuid,
        payload
      ).catch(e => {
        commit("MEDIAS_ERROR", e);
        return Promise.reject(e);
      });

      if (result) {
        commit("MEDIAS_SUCCESS");
      }

      return result;
    },

    // Create image media
    async createImageMedia({ commit }, { project_uuid, media_uuid, payload }) {
      const result = await createImageMedia(
        project_uuid,
        media_uuid,
        payload
      ).catch(e => {
        commit("MEDIAS_ERROR", e);
        return Promise.reject(e);
      });

      if (result) {
        commit("MEDIAS_SUCCESS");
      }

      return result;
    },

    /**
     * Local part
     */

    /**
     * Get medias from IndexedDB
     */
    async get({ commit }, project_uuid) {
      try {
        const mediasCollection = await idb.db.medias.where({
          project_uuid: project_uuid
        });

        commit("SET_MEDIAS", await mediasCollection.toArray());

        return await mediasCollection.toArray();
      } catch (error) {
        throw error;
      }
    },

    /**
     * Adds / Updates a media in IndexedDB
     *
     * @param {*Object} media Media payload
     */
    async create(context, media) {
      try {
        console.log(media);

        return await idb.db.medias.add(media);
      } catch (error) {
        throw error;
      }
    },

    /**
     * Delete media from IndexedDB
     */
    async delete(context, media_uuid) {
      return await idb.db.medias.delete(media_uuid);
    },

    /**
     * Remote part
     */

    /**
     * Get project medias from server
     */
    async getRemoteMedias({ dispatch }, project_uuid) {
      try {
        const { data } = await ProjectsRepo.getProject(project_uuid);

        let medias = [];

        for (const mediaItem of data.medias) {
          let media = await dispatch("getMedia", {
            project_uuid,
            media_uuid: mediaItem.media_uuid
          });

          if (media.has_image) {
            const { image } = await dispatch("getMediaImage", {
              project_uuid,
              media_uuid: mediaItem.media_uuid
            });

            media.image = image;
          }

          medias.push(media);
        }

        return medias;
      } catch (error) {
        throw error;
      }
    },

    /**
     * Get projects medias
     */
    async getProjectMedias({ commit, dispatch }, project_uuid) {
      try {
        // Get project
        const { data } = await ProjectsRepo.getProject(project_uuid);

        const medias = data.medias.map(async item => {
          const media = await dispatch("getMedia", {
            project_uuid,
            media_uuid: item.media_uuid
          });

          item.croquis = media.croquis;
          item.project_uuid = project_uuid;

          // Has image
          if (media.has_image) {
            const mediaImage = await dispatch("getMediaImage", {
              project_uuid,
              media_uuid: item.media_uuid
            });

            item.image = mediaImage.image;
          }

          return item;
        });

        Promise.all(medias).then(function(results) {
          commit("SET_MEDIAS", results);
        });
      } catch (error) {
        if (error.response && error.response.status == 404)
          throw "Project non trouvé";
        throw error;
      }
    },

    /**
     * Get media image
     */
    async getMediaImage(context, { project_uuid, media_uuid }) {
      try {
        const { data } = await ProjectsRepo.getMediaImage(
          project_uuid,
          media_uuid
        );

        return data;
      } catch (error) {
        if (error.response && error.response.status == 404)
          throw "Image non trouvé";
        throw error;
      }
    }
  }
};
